import { useEffect, useState } from "react";
import { Sparkles, Send, Mail, Target, Brain, FileText, MailCheck, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useCountry } from "@/lib/CountryContext";

const MODES = [
  { id: "email", label: "Prospecting Email", icon: Mail,
    desc: "Generate a tailored prospecting email for a UAE company.",
    placeholder: "e.g. Write to Plant Manager of Star Cement about pneumatic conveying retrofit.",
  },
  { id: "next_action", label: "Next Best Action", icon: Target,
    desc: "Get the single best next move on a deal.",
    placeholder: "e.g. We had a call last week about RAK WWTP — what's next?",
  },
  { id: "recommend", label: "Prospect Recommendations", icon: Brain,
    desc: "Rank prospects by fit with Sodimate's portfolio.",
    placeholder: "e.g. Which 3 cement plants in UAE should we approach first?",
  },
  { id: "summary", label: "Meeting Summary", icon: FileText,
    desc: "Turn raw notes into a structured summary.",
    placeholder: "Paste your meeting notes here…",
  },
  { id: "chat", label: "Open Chat", icon: Sparkles,
    desc: "Free-form B2B sales engineering assistant.",
    placeholder: "Ask anything about UAE BD, equipment, prospects…",
  },
];

export default function AIAssistant() {
  const { country } = useCountry();
  const [mode, setMode] = useState("email");
  const [language, setLanguage] = useState("en");
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState("");
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/companies", { params: { country } }),
      api.get("/projects", { params: { country } }),
    ]).then(([a, b]) => { setCompanies(a.data); setProjects(b.data); });
    loadHistory();
  }, [country]);

  const loadHistory = () => {
    api.get("/ai/history").then((r) => setHistory(r.data));
  };

  const current = MODES.find((m) => m.id === mode);

  const ctxFromSelection = () => {
    const ctx = {};
    if (companyId) {
      const c = companies.find((x) => x.id === companyId);
      if (c) {
        ctx.company = c.name;
        ctx.sector = c.sector;
        ctx.emirate = c.emirate;
        ctx.status = c.status;
        if (c.contact_name) ctx.contact = `${c.contact_name} (${c.contact_role || ""})`;
        if (c.notes) ctx.notes = c.notes;
        ctx._company_id = c.id;
      }
    }
    if (projectId) {
      const p = projects.find((x) => x.id === projectId);
      if (p) {
        ctx.project = p.name;
        ctx.project_status = p.status;
        ctx.estimated_value_usd = p.estimated_value_usd;
        if (p.required_equipment?.length)
          ctx.required_equipment = p.required_equipment.join(", ");
        if (p.description) ctx.project_description = p.description;
      }
    }
    return ctx;
  };

  const submit = async () => {
    if (!message && Object.keys(ctxFromSelection()).length === 0) {
      toast.error("Add a context (company/project) or a message");
      return;
    }
    setBusy(true);
    setResult("");
    try {
      const r = await api.post("/ai/assist", {
        mode, language,
        context: ctxFromSelection(),
        message,
      });
      setResult(r.data.response);
      loadHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI error");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(result);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const openSendModal = () => {
    if (!result) return;
    // Try to extract subject — scan first 3 lines, strip markdown bold/headings
    const lines = result.split("\n").slice(0, 3);
    let subject = "";
    for (const raw of lines) {
      const cleaned = raw.replace(/^[*_#\s>]+/, "").replace(/[*_]+$/, "").trim();
      const m = cleaned.match(/^subject\s*:\s*(.+)$/i);
      if (m) {
        subject = m[1].replace(/[*_]+$/, "").trim();
        break;
      }
    }
    setEmailSubject(subject);
    const c = companies.find((x) => x.id === companyId);
    setEmailTo(c?.email || "");
    setShowSendModal(true);
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      // Strip subject line(s) from body — handle markdown variants
      let body = result;
      const lines = body.split("\n");
      let dropUntil = 0;
      for (let i = 0; i < Math.min(3, lines.length); i++) {
        const cleaned = lines[i].replace(/^[*_#\s>]+/, "").trim();
        if (/^subject\s*:/i.test(cleaned)) {
          dropUntil = i + 1;
          break;
        }
      }
      if (dropUntil > 0) {
        body = lines.slice(dropUntil).join("\n").replace(/^\s+/, "");
      }
      await api.post("/email/send", {
        to: emailTo,
        subject: emailSubject,
        body,
        company_id: companyId || null,
      });
      toast.success(`Email sent to ${emailTo}`);
      setShowSendModal(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="ai-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-eyebrow flex items-center gap-2">
            <Sparkles size={12} className="text-[#004B87]" /> Powered by Claude Sonnet 4.5
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">AI Sales Assistant</h1>
          <p className="text-sm text-gray-500 mt-2">Prospecting emails, recommendations &amp; analysis for UAE BD.</p>
        </div>
      </div>

      {/* Mode tabs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-6">
        {MODES.map((m) => (
          <button
            key={m.id}
            onClick={() => { setMode(m.id); setResult(""); }}
            data-testid={`ai-mode-${m.id}`}
            className={`text-left p-3 border transition-colors ${
              mode === m.id
                ? "border-[#004B87] bg-[#004B87]/5"
                : "border-gray-200 bg-white hover:border-gray-300"
            }`}
          >
            <m.icon size={14} className={mode === m.id ? "text-[#004B87]" : "text-gray-400"} />
            <div className={`mt-2 font-semibold text-sm ${mode === m.id ? "text-[#004B87]" : ""}`}>{m.label}</div>
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="section-card p-5" data-testid="ai-input-panel">
          <div className="label-eyebrow">{current.label}</div>
          <p className="text-sm text-gray-600 mt-2 mb-5">{current.desc}</p>

          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <label className="label-eyebrow block mb-2">Language</label>
              <select className="input-stark" value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="en">English</option>
                <option value="fr">Français</option>
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Company</label>
              <select className="input-stark" value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="ai-company-select">
                <option value="">— none —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>

          <div className="mb-3">
            <label className="label-eyebrow block mb-2">Project (optional)</label>
            <select className="input-stark" value={projectId} onChange={(e) => setProjectId(e.target.value)}>
              <option value="">— none —</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>

          <div className="mb-4">
            <label className="label-eyebrow block mb-2">Your request / notes</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={current.placeholder}
              className="input-stark min-h-[140px]"
              data-testid="ai-message-input"
            />
          </div>

          <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center" data-testid="ai-submit-button">
            {busy ? "Thinking…" : <>Generate <Send size={14} /></>}
          </button>
        </div>

        {/* Output */}
        <div className="section-card p-5" data-testid="ai-output-panel">
          <div className="flex items-center justify-between mb-1">
            <div className="label-eyebrow">Output</div>
            {result && (
              <div className="flex items-center gap-2">
                <button onClick={copy} className="text-xs text-gray-500 hover:text-[#004B87] flex items-center gap-1">
                  {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
                </button>
                {mode === "email" && (
                  <button onClick={openSendModal} className="btn-primary text-xs" data-testid="send-email-button">
                    <MailCheck size={12} /> Send via email
                  </button>
                )}
              </div>
            )}
          </div>
          {!result && !busy && (
            <div className="mt-8 text-center text-gray-400 text-sm py-12">
              <Sparkles size={28} className="mx-auto mb-3 text-gray-300" />
              The response will appear here.
            </div>
          )}
          {busy && (
            <div className="mt-8 text-center text-gray-400 text-sm py-12">
              Generating…
            </div>
          )}
          {result && (
            <div className="mt-3 prose prose-sm max-w-none whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
              {result}
            </div>
          )}
        </div>
      </div>

      {/* Send email modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setShowSendModal(false)}>
          <form onSubmit={sendEmail} onClick={(e) => e.stopPropagation()}
            data-testid="send-email-modal"
            className="bg-white border border-gray-200 w-full max-w-xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="label-eyebrow">Email</div>
              <div className="font-display text-xl font-bold mt-1">Send via Resend</div>
            </div>
            <div className="p-6 space-y-3">
              <div>
                <label className="label-eyebrow block mb-2">To</label>
                <input type="email" required className="input-stark"
                  value={emailTo} onChange={(e) => setEmailTo(e.target.value)}
                  data-testid="email-to-input" placeholder="contact@company.com" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Subject</label>
                <input required className="input-stark"
                  value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)}
                  data-testid="email-subject-input" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Preview</label>
                <div className="border border-gray-200 p-3 max-h-48 overflow-y-auto text-xs whitespace-pre-wrap text-gray-600">
                  {result.split("\n").filter((l, i) => !(i === 0 && /^\s*subject\s*:/i.test(l))).join("\n")}
                </div>
              </div>
              <div className="text-[11px] text-gray-500 bg-amber-50 border border-amber-200 p-2">
                ⚠️ In Resend test mode, emails only deliver to verified email addresses. Verify a domain at resend.com to send to clients.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setShowSendModal(false)}>Cancel</button>
              <button type="submit" disabled={sending} className="btn-primary" data-testid="confirm-send-email-button">
                {sending ? "Sending…" : "Send"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="mt-8">
          <div className="label-eyebrow mb-3">Recent</div>
          <div className="space-y-2">
            {history.slice(0, 5).map((h) => (
              <div key={h.id} className="section-card p-4 text-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="chip border-[#004B87] text-[#004B87] bg-[#004B87]/5">{h.mode}</span>
                  <span className="text-xs text-gray-400">{new Date(h.created_at).toLocaleString()}</span>
                </div>
                <div className="text-gray-600 line-clamp-2 whitespace-pre-wrap">{h.response}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
